import {
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { uploadAssets, MAX_UPLOAD_FILE_SIZE_MB } from '../../../utils/uploadAssets';
import { DocumentType } from '../../../enum';
import { resolveAssetUrl } from '../../../utils/url';

interface Props {
  images: string[];
  onChange: (images: string[]) => void;
  thumbnailUrl: string;
  onThumbnailChange: (thumbnailUrl: string) => void;
  accessToken: string;
  assetUrl: string;
}

const MAX_BUNDLE_IMAGES = 5;

const isValidImageFile = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const isImageMime = (file.type || '').startsWith('image/');
  const isKnownImageExt = [
    'png',
    'jpg',
    'jpeg',
    'webp',
    'gif',
    'bmp',
    'svg',
  ].includes(ext);
  return isImageMime || isKnownImageExt;
};

const BundleImagesSection = ({
  images,
  onChange,
  thumbnailUrl,
  onThumbnailChange,
  accessToken,
  assetUrl,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const didDragRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList?.length || images.length >= MAX_BUNDLE_IMAGES) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    const remainingSlots = MAX_BUNDLE_IMAGES - images.length;
    const selectedFiles = Array.from(fileList);

    if (selectedFiles.length > remainingSlots) {
      toast.info(
        `You can add ${remainingSlots} more image(s). Uploading the first ${remainingSlots}.`
      );
    }

    const filesToUpload = selectedFiles.slice(0, remainingSlots);
    const validFiles = filesToUpload.filter(isValidImageFile);
    const skippedCount = filesToUpload.length - validFiles.length;

    if (skippedCount > 0) {
      toast.error(
        `${skippedCount} file(s) skipped — only image files are allowed.`
      );
    }

    if (!validFiles.length) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    const uploadedKeys: string[] = [];

    try {
      for (const file of validFiles) {
        const uploadResult: any = await uploadAssets(
          file,
          DocumentType.AddonProduct,
          accessToken
        );

        if (uploadResult?.data?.keypath) {
          uploadedKeys.push(uploadResult.data.keypath);
        }
      }

      if (uploadedKeys.length) {
        const nextImages = [...images, ...uploadedKeys];
        onChange(nextImages);

        const thumbnailStillValid =
          thumbnailUrl && images.includes(thumbnailUrl);
        if (!thumbnailStillValid) {
          onThumbnailChange(nextImages[0] || '');
        }
      }
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleRemove = (index: number) => {
    const removed = images[index];
    const nextImages = images.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextImages);

    if (removed === thumbnailUrl) {
      onThumbnailChange(nextImages[0] || '');
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (result.destination && result.source.index !== result.destination.index) {
      const nextImages = Array.from(images);
      const [moved] = nextImages.splice(result.source.index, 1);
      nextImages.splice(result.destination.index, 0, moved);
      onChange(nextImages);
    }

    // Avoid treating a completed drag as a thumbnail click
    window.setTimeout(() => {
      didDragRef.current = false;
    }, 0);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant='subtitle1'
        sx={{ fontWeight: 700, mb: 0.5 }}
      >
        Bundle Images
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ mb: 2 }}
      >
        These images will appear on the home page of the mobile application.
        Drag an image to reorder. Click an image to set it as the product card
        thumbnail.
      </Typography>

      <DragDropContext
        onDragStart={() => {
          didDragRef.current = true;
        }}
        onDragEnd={handleDragEnd}
      >
        <Droppable
          droppableId='bundle-images'
          direction='horizontal'
        >
          {(droppableProvided) => (
            <Stack
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              direction='row'
              spacing={2}
              flexWrap='wrap'
              useFlexGap
            >
              {images.map((image, index) => {
                const isThumbnail = thumbnailUrl === image;
                return (
                  <Draggable
                    key={`${image}-${index}`}
                    draggableId={`bundle-image-${index}`}
                    index={index}
                  >
                    {(draggableProvided, snapshot) => (
                      <Box
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        onClick={() => {
                          if (didDragRef.current || snapshot.isDragging) return;
                          onThumbnailChange(image);
                        }}
                        sx={{
                          position: 'relative',
                          width: 96,
                          height: 96,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '2px solid',
                          borderColor: isThumbnail
                            ? 'primary.main'
                            : snapshot.isDragging
                              ? 'info.main'
                              : 'divider',
                          cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                          boxShadow: snapshot.isDragging
                            ? 3
                            : isThumbnail
                              ? 1
                              : 0,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box
                          component='img'
                          src={resolveAssetUrl(image, assetUrl)}
                          alt={`Bundle ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            pointerEvents: 'none',
                          }}
                        />

                        <IconButton
                          size='small'
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemove(index);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            zIndex: 2,
                            bgcolor: 'rgba(0,0,0,0.55)',
                            color: 'common.white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                          }}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>

                        {isThumbnail && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              fontSize: 10,
                              fontWeight: 700,
                              textAlign: 'center',
                              py: 0.25,
                            }}
                          >
                            Thumbnail
                          </Box>
                        )}
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}

              {images.length < MAX_BUNDLE_IMAGES && (
                <Box
                  onClick={() => {
                    if (!isUploading) inputRef.current?.click();
                  }}
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: 1,
                    border: '2px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isUploading ? 'wait' : 'pointer',
                    opacity: isUploading ? 0.6 : 1,
                    color: 'text.secondary',
                  }}
                >
                  <AddIcon />
                  <Typography variant='caption'>
                    {isUploading ? 'Uploading…' : 'Upload'}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ mt: 1, display: 'block' }}
      >
        {images.length}/{MAX_BUNDLE_IMAGES} images · Max{' '}
        {MAX_UPLOAD_FILE_SIZE_MB}MB per image · You can select multiple files
        at once
      </Typography>

      <input
        ref={inputRef}
        type='file'
        multiple
        accept='image/png,image/jpeg,image/jpg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif'
        hidden
        onChange={handleUpload}
      />
    </Box>
  );
};

export default BundleImagesSection;
